import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Copy, Check, AlertTriangle, Pencil } from 'lucide-react';
import Card from '../../components/common/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import Button from '../../components/common/Button';
import Toast from '../../components/common/Toast';
import { getTeamById, getBucketPolicySnippet, disableTeam, enableTeam, deleteTeam, updateTeam } from '../../services/teamService';
import './Teams.css';

/**
 * TeamDetail Page (Task 12.3)
 *
 * Shows team fields plus the bucket policy snippet with a copy button.
 * Disable / Re-enable / Delete actions.
 * Delete is disabled when is_default = true (shows reason: default_team_not_deletable).
 *
 * Requirements: R9.3, R1.5, R2.4, R2.6
 */
export default function TeamDetail() {
  const { teamId } = useParams();
  const navigate = useNavigate();

  const [team, setTeam] = useState(null);
  const [snippet, setSnippet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snippetLoading, setSnippetLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});

  const fetchTeam = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTeamById(teamId);
      setTeam(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  const fetchSnippet = useCallback(async () => {
    try {
      setSnippetLoading(true);
      const data = await getBucketPolicySnippet(teamId);
      setSnippet(data?.snippet || data?.policy_statement || JSON.stringify(data, null, 2));
    } catch {
      setSnippet(null);
    } finally {
      setSnippetLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchTeam();
    fetchSnippet();
  }, [fetchTeam, fetchSnippet]);

  const handleCopy = async () => {
    if (!snippet) return;
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setToast({ message: 'Failed to copy to clipboard', type: 'error' });
    }
  };

  const handleDisable = async () => {
    if (!window.confirm(`Disable team "${team.name}"? This will remove their bucket policy access. Existing data is preserved.`)) return;
    try {
      setActionLoading('disable');
      await disableTeam(teamId);
      setToast({ message: 'Team disabled', type: 'success' });
      fetchTeam();
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to disable team', type: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnable = async () => {
    try {
      setActionLoading('enable');
      await enableTeam(teamId);
      setToast({ message: 'Team re-enabled', type: 'success' });
      fetchTeam();
      fetchSnippet();
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to re-enable team', type: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete team "${team.name}"? This cannot be undone.`)) return;
    try {
      setActionLoading('delete');
      await deleteTeam(teamId);
      setToast({ message: 'Team deleted', type: 'success' });
      setTimeout(() => navigate('/admin/teams'), 1500);
    } catch (err) {
      const msg = err.response?.data?.reason === 'default_team_not_deletable'
        ? 'The default team cannot be deleted'
        : err.response?.data?.message || 'Failed to delete team';
      setToast({ message: msg, type: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const startEditing = () => {
    setEditForm({
      name: team.name || '',
      description: team.description || '',
      contact_email: team.contact_email || '',
      source_account_id: team.source_account_id || ''
    });
    setEditErrors({});
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditForm({});
    setEditErrors({});
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
    if (editErrors[name]) {
      setEditErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSave = async () => {
    const errors = {};
    if (!editForm.name?.trim()) errors.name = 'Name is required';
    if (!editForm.contact_email?.trim()) errors.contact_email = 'Contact email is required';
    if (editForm.source_account_id && !/^\d{12}$/.test(editForm.source_account_id.trim())) {
      errors.source_account_id = 'Must be exactly 12 digits';
    }
    if (Object.keys(errors).length) {
      setEditErrors(errors);
      return;
    }

    try {
      setActionLoading('save');
      await updateTeam(teamId, editForm);
      setToast({ message: 'Team updated successfully', type: 'success' });
      setEditing(false);
      fetchTeam();
      fetchSnippet();
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to update team', type: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="teams-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="teams-page">
        <ErrorMessage message={error} onRetry={fetchTeam} />
      </div>
    );
  }

  if (!team) return null;

  return (
    <div className="teams-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <button className="back-link" onClick={() => navigate('/admin/teams')}>
              <ArrowLeft size={20} />
              Back to Teams
            </button>
            <h1 className="page-title">
              <Shield size={32} />
              {team.name}
              {team.is_default && <span className="default-badge">Default</span>}
              <span className={`status-pill status-${team.status}`}>{team.status}</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Team Details */}
      <Card className="teams-card">
        <div className="card-section-header">
          <h2 className="card-section-title">Team Details</h2>
          {!editing && (
            <Button variant="secondary" size="small" onClick={startEditing}>
              <Pencil size={14} /> Edit
            </Button>
          )}
        </div>

        {editing ? (
          <div className="team-edit-form">
            <div className="form-group">
              <label htmlFor="edit-name">Display Name</label>
              <input id="edit-name" name="name" value={editForm.name} onChange={handleEditChange} className={editErrors.name ? 'input-error' : ''} />
              {editErrors.name && <span className="field-error">{editErrors.name}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="edit-source-account">Source Account ID</label>
              <input id="edit-source-account" name="source_account_id" value={editForm.source_account_id} onChange={handleEditChange} placeholder="123456789012" className={editErrors.source_account_id ? 'input-error' : ''} />
              {editErrors.source_account_id && <span className="field-error">{editErrors.source_account_id}</span>}
              <span className="field-hint">Changing this will update the bucket policy to grant the new account access.</span>
            </div>
            <div className="form-group">
              <label htmlFor="edit-email">Contact Email</label>
              <input id="edit-email" name="contact_email" value={editForm.contact_email} onChange={handleEditChange} className={editErrors.contact_email ? 'input-error' : ''} />
              {editErrors.contact_email && <span className="field-error">{editErrors.contact_email}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="edit-desc">Description</label>
              <textarea id="edit-desc" name="description" value={editForm.description} onChange={handleEditChange} rows={3} />
            </div>
            <div className="form-actions">
              <Button variant="primary" onClick={handleSave} disabled={actionLoading === 'save'}>
                {actionLoading === 'save' ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="secondary" onClick={cancelEditing} disabled={actionLoading === 'save'}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="team-detail-grid">
            <div className="detail-item">
              <span className="detail-label">Team ID</span>
              <span className="detail-value mono">{team.team_id}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Display Name</span>
              <span className="detail-value">{team.name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Source Account ID</span>
              <span className="detail-value mono">{team.source_account_id || '—'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Contact Email</span>
              <span className="detail-value">{team.contact_email}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Trigger Bucket</span>
              <span className="detail-value mono">{team.trigger_bucket || '—'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Trigger Prefix</span>
              <span className="detail-value mono">{team.trigger_prefix || '—'}</span>
            </div>
            {team.description && (
              <div className="detail-item detail-item-full">
                <span className="detail-label">Description</span>
                <span className="detail-value">{team.description}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">Created</span>
              <span className="detail-value">{team.created_at ? new Date(team.created_at).toLocaleDateString() : '—'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Created By</span>
              <span className="detail-value">{team.created_by || '—'}</span>
            </div>
          </div>
        )}
      </Card>

      {/* Bucket Policy Snippet */}
      <Card className="teams-card">
        <div className="snippet-header">
          <h2 className="card-section-title">Bucket Policy Snippet</h2>
          <p className="snippet-hint">
            Give this policy statement to the source team. They add it to their S3 bucket policy to allow writes into the shared trigger bucket.
          </p>
        </div>
        {snippetLoading ? (
          <LoadingSpinner />
        ) : snippet ? (
          <div className="snippet-container">
            <pre className="snippet-code">{snippet}</pre>
            <button className="snippet-copy-btn" onClick={handleCopy} title="Copy to clipboard">
              {copied ? <Check size={18} /> : <Copy size={18} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        ) : (
          <p className="snippet-unavailable">Bucket policy snippet not available. The team service may not be deployed yet.</p>
        )}
      </Card>

      {/* Actions */}
      <Card className="teams-card">
        <h2 className="card-section-title">Actions</h2>
        <div className="team-actions">
          {team.status === 'active' ? (
            <Button
              variant="secondary"
              onClick={handleDisable}
              disabled={!!actionLoading}
            >
              {actionLoading === 'disable' ? 'Disabling...' : 'Disable Team'}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleEnable}
              disabled={!!actionLoading}
            >
              {actionLoading === 'enable' ? 'Enabling...' : 'Re-enable Team'}
            </Button>
          )}

          <div className="delete-action">
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={!!actionLoading || team.is_default}
            >
              {actionLoading === 'delete' ? 'Deleting...' : 'Delete Team'}
            </Button>
            {team.is_default && (
              <p className="delete-reason">
                <AlertTriangle size={14} />
                The default team cannot be deleted
              </p>
            )}
          </div>
        </div>
      </Card>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
    </div>
  );
}
